import { ArrowRight, Play, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import Reveal from "@/components/landing/Reveal";
import { useI18n } from "@/lib/i18n";
import { HERO_SCREENSHOT_URL } from "@/data/landing";

export default function Hero() {
  const { t } = useI18n();
  return (
    <section
      data-testid="hero-section"
      id="hero"
      className="relative pt-28 sm:pt-36 pb-20 sm:pb-32 overflow-hidden"
    >
      {/* Soft organic gradients */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-32 -left-24 h-[28rem] w-[28rem] rounded-full bg-emerald-200/50 dark:bg-emerald-800/20 blur-3xl" />
        <div className="absolute top-40 -right-32 h-[24rem] w-[24rem] rounded-full bg-orange-200/60 dark:bg-orange-500/10 blur-3xl" />
        <div className="absolute inset-0 grain opacity-[0.5] mix-blend-multiply dark:mix-blend-screen" />
      </div>

      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
        <div className="lg:col-span-7">
          <Reveal>
            <div
              data-testid="hero-eyebrow"
              className="inline-flex items-center gap-2 rounded-full border border-emerald-800/15 dark:border-emerald-400/20 bg-white/70 dark:bg-neutral-900/60 px-3.5 py-1.5"
            >
              <span className="relative inline-flex h-2 w-2">
                <span className="absolute inset-0 rounded-full bg-emerald-500 animate-pulse-ring" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-600" />
              </span>
              <span className="text-xs uppercase tracking-[0.22em] font-bold text-emerald-800 dark:text-emerald-400">
                {t("hero.eyebrow")}
              </span>
            </div>
          </Reveal>

          <Reveal delay={0.05}>
            <h1
              data-testid="hero-headline"
              className="mt-6 font-display font-bold text-balance text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] text-neutral-900 dark:text-neutral-50"
            >
              {t("hero.h1Pre")}
              <br />
              <span className="text-emerald-800 dark:text-emerald-400">
                {t("hero.h1Highlight")}
              </span>{" "}
              {t("hero.h1Post")}
            </h1>
          </Reveal>

          <Reveal delay={0.12}>
            <p
              data-testid="hero-subhead"
              className="mt-6 text-lg sm:text-xl text-neutral-600 dark:text-neutral-300 max-w-xl leading-relaxed"
            >
              {t("hero.sub")}
            </p>
          </Reveal>

          <Reveal delay={0.18}>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Button
                asChild
                data-testid="hero-cta-primary"
                className="h-14 rounded-full px-7 text-base font-semibold bg-emerald-800 hover:bg-emerald-900 text-stone-50 hover:text-white pill-shadow hover:-translate-y-0.5 transition"
              >
                <a href="#download" className="inline-flex items-center gap-2">
                  {t("hero.ctaPrimary")}
                  <ArrowRight className="h-4 w-4" />
                </a>
              </Button>
              <Button
                asChild
                variant="ghost"
                data-testid="hero-cta-secondary"
                className="h-14 rounded-full px-6 text-base font-semibold border border-neutral-300 dark:border-neutral-700 bg-white/60 dark:bg-neutral-900/60 hover:bg-white dark:hover:bg-neutral-900 text-neutral-900 dark:text-neutral-50 hover:-translate-y-0.5 transition"
              >
                <a href="#how-it-works" className="inline-flex items-center gap-2">
                  <Play className="h-4 w-4 fill-current" />
                  {t("hero.ctaSecondary")}
                </a>
              </Button>
            </div>
          </Reveal>

          <Reveal delay={0.24}>
            <div
              data-testid="hero-trust-line"
              className="mt-8 flex items-center gap-3 text-sm text-neutral-600 dark:text-neutral-400"
            >
              <ShieldCheck className="h-4 w-4 text-emerald-700 dark:text-emerald-400 shrink-0" />
              <span>{t("hero.trust")}</span>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-5">
          <Reveal delay={0.18} y={32}>
            <div className="relative mx-auto w-full max-w-sm">
              <div className="absolute -inset-6 -z-10 rounded-[3rem] bg-gradient-to-br from-emerald-200/70 via-stone-100 to-orange-200/60 dark:from-emerald-700/30 dark:via-neutral-900 dark:to-orange-500/20 blur-2xl" />
              <PhoneFrame>
                <img
                  src={HERO_SCREENSHOT_URL}
                  alt={t("hero.shotAlt")}
                  loading="eager"
                  decoding="async"
                  className="absolute inset-0 h-full w-full object-cover"
                  data-testid="hero-app-screenshot"
                />
              </PhoneFrame>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function PhoneFrame({ children }) {
  return (
    <div
      data-testid="hero-phone-frame"
      className="relative aspect-[9/19] w-full rounded-[2.4rem] bg-neutral-950 p-2.5 ring-1 ring-neutral-800 shadow-[0_30px_80px_-20px_rgba(6,95,70,0.45)] animate-soft-float"
    >
      <div className="relative h-full w-full rounded-[2rem] overflow-hidden bg-stone-50 dark:bg-neutral-900">
        <div className="absolute top-2 left-1/2 -translate-x-1/2 h-5 w-24 rounded-full bg-neutral-950 z-20" />
        {children}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/15 via-transparent to-transparent dark:from-white/5" />
      </div>
    </div>
  );
}
